import RouteContainer from 'components/RouteContainer';
import { PATHS } from 'constants/paths';
import React, { lazy } from 'react';
import { Redirect, Switch } from 'react-router-dom';

const Home = lazy(() => import('./pages/Home'));
const WellHome = lazy(() => import('./pages/WellHome'));
const KickPrediction = lazy(() => import('./pages/KickPrediction'));
const KickDetection = lazy(() => import('./pages/KickDetection'));
const StuckPrediction = lazy(() => import('./pages/StuckPrediction'));
const WellPlansList = lazy(() => import('./pages/Performance/WellPlansList'));
const Planning = lazy(() => import('./pages/Performance/Planning'));
const RealTime = lazy(() => import('./pages/Performance/RealTime'));
const RealTimeDeepDive = lazy(() => import('./pages/Performance/RealTimeDeepDive'));
const GlobalBenchmark = lazy(() => import('./pages/Performance/GlobalBenchmark'));
const WellSettings = lazy(() => import('./pages/WellSettings'));
const UserAdmin = lazy(() => import('./pages/UserAdmin'));
const WellboreAdmin = lazy(() => import('./pages/WellboreAdmin'));
const EddrOverview = lazy(() => import('./pages/Eddr/Overview'));
const EddrOperationsCurves = lazy(() => import('./pages/Eddr/OperationsCurves'));
const EddrProgressCurves = lazy(() => import('./pages/Eddr/EddrProgressCurves'));
const EddrOperationsLogs = lazy(() => import('./pages/Eddr/OperationsLogs'));
const EddrOperationalLogsMobile = lazy(() => import('./pages/Eddr/OperationsLogs/mobile'));
const EddrSafety = lazy(() => import('./pages/Eddr/Safety'));
const EddrHome = lazy(() => import('./pages/Eddr/Home'));
const GlobalFeedbackPage = lazy(() => import('./pages/GlobalFeedbackPage'));
const EddrFluids = lazy(() => import('./pages/Eddr/Fluids'));
const EddrTrajectory = lazy(() => import('./pages/Eddr/Trajectory'));
const EddrBHA = lazy(() => import('./pages/Eddr/BHA'));
const EddrWellActivityHistory = lazy(() => import('./pages/Eddr/WellActivityHistory'));
const DataManagementDataQuality = lazy(() => import('./pages/DataManagementDataQuality'));
const DataManagementTaggingTool = lazy(() => import('./pages/TaggingTool'));
const NewDataset = lazy(() => import('./pages/NewDataset'));
const DataManagementTrainingDatasets = lazy(() => import('./pages/TrainingDatasets'));
const Anticollision = lazy(() => import('./pages/Anticollision'));
const LessonsLearntRegister = lazy(() => import('./pages/LessonsLearnt/LessonsLearntRegister'));
const LessonsLearntWell = lazy(() => import('./pages/LessonsLearnt/LessonsLearntWell'));
const LessonsLearntView = lazy(() => import('./pages/LessonsLearnt/LessonsLearntView'));
const DrillxCockpit = lazy(() => import('./pages/DrillxCockpit'));
const WgWellSingleSchematic = lazy(() => import('./pages/WellGuardian/WellSingleSchematic'));
const WgWellSingleOverview = lazy(() => import('./pages/WellGuardian/WellSingleOverview'));
const WgWellCharts = lazy(() => import('./pages/WellGuardian/WellCharts'));
const WgWellReviews = lazy(() => import('./pages/WellGuardian/wellReviews'));
const WgWellBehaviour = lazy(() => import('./pages/WellGuardian/WellBehaviour'));
const WgWelldgshse = lazy(() => import('./pages/WellGuardian/Dgshse'));
const WgWellResource = lazy(() => import('./pages/WellGuardian/WellResources'));
const WgSubscription = lazy(() => import('./pages/WellGuardian/AlertsSubscription'));
const WgDerogations = lazy(() => import('./pages/WellGuardian/Derogations'));
const WgSynergiCases = lazy(() => import('./pages/WellGuardian/SynergiCases'));
const WgKeyLinks = lazy(() => import('./pages/WellGuardian/KeyLinks'));
const WgSingleWellAlerts = lazy(() => import('./pages/WellGuardian/WellAlerts/SingleWellAlerts'));
const WgMultipleWellsAlerts = lazy(
  () => import('./pages/WellGuardian/WellAlerts/MultipleWellsAlerts'),
);
const WgAllWells = lazy(() => import('./pages/WellGuardian/AllWells'));
const WgWIRInterventions = lazy(() => import('./pages/WellGuardian/WIR/WIRInterventions'));
const WgWIRCreation = lazy(() => import('./pages/WellGuardian/WIR/WIRCreation'));
const EnergyEfficiencyHomePage = lazy(() => import('./pages/EnergyEfficiency/EnergyEffciencyHome'));
const WellVisualisationPlannedDataForm = lazy(
  () => import('./pages/WellVisualisationPlannedDataForm'),
);

const GhostMode = lazy(() => import('./pages/GhostMode'));
const AdminPo = lazy(() => import('./pages/AdminPo'));

const Routes: React.FC = () => {
  const browserDetail = navigator.userAgent;

  const isMobile =
    /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(browserDetail) ||
    /\b(Android|Windows Phone|iPad|iPod)\b/i.test(browserDetail);

  return (
    <Switch>
      <RouteContainer
        exact
        isMobile={isMobile}
        withMobileWellSelect={false}
        path={PATHS.HOME}
        component={isMobile ? EddrHome : Home}
      />
      <RouteContainer
        isMobile={isMobile}
        path={PATHS.WELL_HOME}
        withMobileWellSelect={isMobile ? false : true}
        component={WellHome}
      />
      <RouteContainer path={PATHS.KICK_PREDICTION} component={KickPrediction} />
      <RouteContainer path={PATHS.STUCK_PIPE_PREDICTION} component={StuckPrediction} />
      <RouteContainer path={PATHS.KICK_DETECTION} component={KickDetection} />
      <RouteContainer path={PATHS.DRILLX_COCKPIT} component={DrillxCockpit} />
      <RouteContainer path={PATHS.LIVE_PERFORMANCE} component={RealTimeDeepDive} />
      <RouteContainer path={PATHS.GLOBAL_BENCHMARK} component={GlobalBenchmark} />
      <RouteContainer path={PATHS.PROGRESS_FOLLOW_UP} component={RealTime} />
      <RouteContainer path={PATHS.PLANNING_CATALOGUE} component={WellPlansList} />
      <RouteContainer path={PATHS.PLANNING_EDIT} component={Planning} />
      <RouteContainer path={PATHS.EDDR_OVERVIEW} isMobile={isMobile} component={EddrOverview} />
      <RouteContainer
        path={PATHS.EDDR_OPERATIONS_LOG}
        isMobile={isMobile}
        component={isMobile ? EddrOperationalLogsMobile : EddrOperationsLogs}
      />
      <RouteContainer path={PATHS.EDDR_OPERATIONS_CURVES} component={EddrOperationsCurves} />
      <RouteContainer path={PATHS.EDDR_PROGRESS_CURVE} component={EddrProgressCurves} />
      <RouteContainer path={PATHS.EDDR_FLUIDS} component={EddrFluids} />
      <RouteContainer path={PATHS.EDDR_SAFETY} component={EddrSafety} />
      <RouteContainer path={PATHS.EDDR_TRAJECTORY} component={EddrTrajectory} />
      <RouteContainer path={PATHS.EDDR_BHA} component={EddrBHA} />
      <RouteContainer path={PATHS.EDDR_WELL_ACTIVITY_HISTORY} component={EddrWellActivityHistory} />
      <RouteContainer path={PATHS.LESSONS_LEARNT_REGISTER} component={LessonsLearntRegister} />
      <RouteContainer path={PATHS.LESSONS_LEARNT_WELL} component={LessonsLearntWell} />
      <RouteContainer path={PATHS.LESSONS_LEARNT_VIEW} component={LessonsLearntView} />
      <RouteContainer path={PATHS.USER_ADMIN} component={UserAdmin} />
      <RouteContainer path={PATHS.WELLBORE_ADMIN} component={WellboreAdmin} />
      <RouteContainer path={PATHS.WELL_SETTINGS} component={WellSettings} />
      <Redirect from={PATHS.WELL_GUARDIAN} to={PATHS.WELL_GUARDIAN_WELL_HEALTH_HOME} exact />
      <RouteContainer path={PATHS.WELL_GUARDIAN_WELL_HEALTH_HOME} exact component={WgAllWells} />
      <RouteContainer
        path={PATHS.WELL_GUARDIAN_INTERVENTIONS}
        exact
        component={WgWIRInterventions}
      />
      <RouteContainer
        path={PATHS.WELL_GUARDIAN_WELL_SINGLE_OVERVIEW}
        component={WgWellSingleOverview}
      />
      <RouteContainer path={PATHS.WELL_GUARDIAN_WELL_WELL_REVIEWS} component={WgWellReviews} />
      <RouteContainer path={PATHS.WELL_GUARDIAN_WELL_SINGLE_CHARTS} component={WgWellCharts} />
      <RouteContainer
        path={PATHS.WELL_GUARDIAN_WELL_SINGLE_SCHEMATIC}
        component={WgWellSingleSchematic}
      />
      <RouteContainer path={PATHS.WELL_GUARDIAN_WELL_BEHAVIOUR} component={WgWellBehaviour} />
      <RouteContainer path={PATHS.WELL_GUARDIAN_INTEGRITY_DETAILS} component={WgWelldgshse} />
      <RouteContainer path={PATHS.WELL_GUARDIAN_WELL_RESOURCES} component={WgWellResource} />
      <RouteContainer
        path={PATHS.WELL_GUARDIAN_SINGLE_WELL_ALERTS}
        component={WgSingleWellAlerts}
      />
      <RouteContainer
        path={PATHS.WELL_GUARDIAN_MULTIPLE_WELLS_ALERTS}
        component={WgMultipleWellsAlerts}
      />
      <RouteContainer path={PATHS.WELL_GUARDIAN_ALERTS_SUBSCRIPTION} component={WgSubscription} />
      <RouteContainer path={PATHS.WELL_GUARDIAN_DEROGATIONS} component={WgDerogations} />
      <RouteContainer path={PATHS.WELL_GUARDIAN_SYNERGI_CASES} component={WgSynergiCases} />
      <RouteContainer path={PATHS.WELL_GUARDIAN_KEY_LINK} component={WgKeyLinks} />
      <RouteContainer path={PATHS.WELL_GUARDIAN_WIR_CREATION} component={WgWIRCreation} />
      <RouteContainer path={PATHS.DATA_MANAGEMENT} component={DataManagementDataQuality} />
      <RouteContainer path={PATHS.GLOBAL_FEEDBACK} component={GlobalFeedbackPage} />
      <Redirect exact from={PATHS.DDX_PO} to={PATHS.DDX_TRAINING_DATASETS} />
      <RouteContainer path={PATHS.DDX_TAGGING_TOOL} component={DataManagementTaggingTool} />
      <RouteContainer path={PATHS.DDX_NEW_DATASET} component={NewDataset} />
      <RouteContainer
        path={PATHS.DDX_TRAINING_DATASETS}
        component={DataManagementTrainingDatasets}
      />
      <RouteContainer path={PATHS.ANTICOLLISION} component={Anticollision} />
      <RouteContainer
        path={PATHS.WELL_VISUALISATION_PLANNED_DATA_FORM}
        component={WellVisualisationPlannedDataForm}
      />
      <RouteContainer path={PATHS.GHOST_MODE} component={GhostMode} />
      <RouteContainer path={PATHS.ADMIN_PO} component={AdminPo} />
      <RouteContainer
        path={PATHS.ENERGY_EFFICIENCY_HOME}
        exact
        component={EnergyEfficiencyHomePage}
      />
    </Switch>
  );
};
export default Routes;
